/**
 * DSA Payout Offer — Detail Page (T3 Detail).
 *
 * Mirrors the marketplace ListingDetail polished structure:
 *   • Breadcrumb header bar (bank · product · status)
 *   • Top zone (lg:col-8 + lg:col-4):
 *       - Overview Card: bank hero, location, commission + est-payout band,
 *         quick-stats grid (views/enquiries/closed/closing-in)
 *       - DSA Profile Card (avatar, headline, rating/deals/exp/member-since/
 *         response-time/total-disbursed/languages)
 *       - sticky Action Card (Enquire / owner actions)
 *   • Full-width collapsible Panels below:
 *       - Payout Structure (type, timeline, slabs, cap)
 *       - Eligibility Criteria (employment, age, income, tenure, CIBIL)
 *       - Operations & Trust (approval rate, processing, digital journey,
 *         documents required, foreclosure norms)
 *       - Coverage & Specializations
 *       - Timeline
 *   • Mobile sticky Enquire bar at the bottom.
 *
 * All new richer fields (payout/eligibility/operations) + inherited lender
 * meta (lender_type/approval_rate/loan_type_category) + DSA profile
 * enrichment (member_since/response_time/deals_closed/total_disbursed/
 * languages) surface here; null-safe — missing fields show "—" / "Any" /
 * "Not specified" in muted text rather than collapsing the row.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import {
    dsaOfferService,
    getDsaOfferStatusConfig,
    getDsaLoanTypeConfig,
    formatCommission,
    formatLoanRange,
    getDeadlineUrgency,
    formatTimeAgo,
} from '../../services/dsaOfferService';
import { InviteToLeadDialog } from './components';

const LENDER_TYPE_SEVERITY = { nbfc: 'help', fintech: 'warning' };
const EMPLOYMENT_LABEL = { salaried: 'Salaried', self_employed: 'Self-Employed', both: 'Salaried & Self-Emp' };

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const OfferDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);

    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showInviteDialog, setShowInviteDialog] = useState(false);


    useEffect(() => {
        let cancelled = false;
        const fetchOfferDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await dsaOfferService.getOfferDetails(id);
                if (cancelled) return;
                if (response.success) {
                    setOffer(response.data);
                } else {
                    setError(response.message || 'Failed to load offer details');
                }
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load offer details');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchOfferDetails();
        return () => { cancelled = true; };
    }, [id]);

    const reload = async () => {
        try {
            const response = await dsaOfferService.getOfferDetails(id);
            if (response.success) setOffer(response.data);
        } catch (err) {
            console.error('Reload failed:', err?.message);
        }
    };

    const handlePause = () => {
        confirmDialog({
            message: 'Pause this offer? It will not be visible to Lead Owners.',
            header: 'Pause Offer',
            icon: 'pi pi-pause',
            acceptClassName: 'p-button-warning',
            accept: async () => {
                setActionLoading(true);
                try {
                    const r = await dsaOfferService.pauseOffer(id);
                    if (r.success) {
                        toast.current?.show({ severity: 'success', summary: 'Offer Paused', life: 2000 });
                        reload();
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message });
                } finally { setActionLoading(false); }
            },
        });
    };

    const handleResume = async () => {
        setActionLoading(true);
        try {
            const r = await dsaOfferService.resumeOffer(id);
            if (r.success) {
                toast.current?.show({ severity: 'success', summary: 'Offer Resumed', life: 2000 });
                reload();
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message });
        } finally { setActionLoading(false); }
    };

    const handleDelete = () => {
        confirmDialog({
            message: 'Delete this offer? This action cannot be undone.',
            header: 'Delete Offer',
            icon: 'pi pi-trash',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setActionLoading(true);
                try {
                    const r = await dsaOfferService.deleteOffer(id);
                    if (r.success) {
                        toast.current?.show({ severity: 'success', summary: 'Offer Deleted', life: 2000 });
                        setTimeout(() => navigate('/app/dsa-offers/my-offers'), 1200);
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message });
                } finally { setActionLoading(false); }
            },
        });
    };

    const handleInviteSuccess = () => {
        setShowInviteDialog(false);
        toast.current?.show({ severity: 'success', summary: 'Enquiry Sent', detail: 'Your enquiry has been sent to the payout', life: 2500 });
    };

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-6">
                <i className="pi pi-exclamation-circle text-red-500 pi-2x mb-3" aria-hidden="true" />
                <h3 className="text-900 m-0 mb-1 text-base">Offer not found</h3>
                <p className="text-600 text-sm mb-3">{error}</p>
                <Button label="Go back" icon="pi pi-arrow-left" size="small" outlined severity="secondary" onClick={() => navigate(-1)} />
            </div>
        );
    }
    if (!offer) return null;

    // ── Destructure offer fields (all null-safe) ──
    const {
        bank_name,
        loan_type,
        segment,
        commission_percentage,
        min_loan_amount,
        max_loan_amount,
        min_cibil,
        processing_time,
        city,
        state,
        disbursement_deadline,
        valid_until,
        status = 'active',
        view_count = 0,
        invite_count = 0,
        acceptance_count = 0,
        created_at,
        posted_by,
        // Inherited from lender / loan_type masters
        lender_type,
        loan_type_category,
        approval_rate,
        // Richer fields (Phase B)
        payout_type,
        payout_on,
        payout_timeline,
        payout_slabs,
        max_payout_cap,
        est_payout_min,
        est_payout_max,
        eligible_employment,
        min_age,
        max_age,
        min_monthly_income,
        tenure_months_min,
        tenure_months_max,
        digital_journey,
        documents_required,
        foreclosure_norms,
    } = offer;

    const service_areas = offer.service_areas || [];
    const specializations = offer.specializations || [];
    const docsRequired = Array.isArray(documents_required) ? documents_required : [];
    const slabs = Array.isArray(payout_slabs) ? payout_slabs : [];

    const loanTypeConfig = getDsaLoanTypeConfig(loan_type);
    const statusConfig = getDsaOfferStatusConfig(status);
    const deadlineInfo = getDeadlineUrgency(disbursement_deadline);

    // ── DSA profile (sourced from AgentProfile via posted_by) ──
    const dsa = posted_by || offer.dsa || {};
    // Owner detection — TRUSTED from the backend (`is_owner` is computed
    // server-side by comparing the authenticated viewer's agent_profile.id
    // with the offer's posted_by_profile_id). No localStorage / id-matching
    // games on the client.
    const isOwner = !!offer.is_owner;
    const dsaName = dsa.display_name || dsa.full_name || dsa.name || 'Unknown DSA';
    const dsaPhoto = dsa.profile_photo_url_full || dsa.avatar || null;
    const dsaRating = dsa.rating != null && !Number.isNaN(Number(dsa.rating)) ? Number(dsa.rating) : null;
    const dsaDeals = dsa.deals_closed ?? dsa.completed_deals_count ?? 0;
    const dsaExperience = dsa.experience_years ?? dsa.years_of_experience ?? null;
    const dsaMemberSince = dsa.member_since || null;
    const dsaResponse = dsa.response_time || null;
    const dsaDisbursed = dsa.total_disbursed ?? dsa.total_loans_disbursed ?? null;
    const dsaLanguages = Array.isArray(dsa.languages)
        ? dsa.languages.map((l) => typeof l === 'string' ? l : (l?.language_name || ''))
            .filter(Boolean)
        : [];
    const dsaHeadline = dsa.headline || dsa.current_designation || null;
    const isVerified = dsa.is_verified || false;

    const employmentLabel = eligible_employment ? EMPLOYMENT_LABEL[eligible_employment] || eligible_employment : null;
    const approvalPct = approval_rate != null
        ? (typeof approval_rate === 'string' ? parseFloat(approval_rate) : Number(approval_rate))
        : null;
    const hasEstPayout = est_payout_min != null && est_payout_max != null && est_payout_max > 0;
    const locationLabel = [city, state].filter(Boolean).join(', ');

    return (
        <div className="grid">
            <div className="col-12">
                <Toast ref={toast} />
                <ConfirmDialog />

                {/* ── Breadcrumb header bar ── */}
                <div className="surface-card border-round shadow-1 p-2 mb-2 md:mb-3 flex align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 min-w-0">
                        <Button icon="pi pi-arrow-left" outlined severity="secondary" size="small" onClick={() => navigate(-1)} aria-label="Go back" />
                        <div className="flex align-items-center gap-1 flex-wrap min-w-0">
                            <span className="text-500 text-xs">Payout offer</span>
                            <span className="text-400">·</span>
                            <span className="text-700 text-xs font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">{bank_name}</span>
                            {(loan_type_category || loanTypeConfig.label) && (
                                <>
                                    <span className="text-400">·</span>
                                    <span className="text-700 text-xs">{loan_type_category || loanTypeConfig.label}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <Tag value={statusConfig.label} severity={statusConfig.severity} className="text-xs capitalize flex-shrink-0" />
                </div>

                <div className="grid">
                    {/* ── Top zone: col-8 Overview ── */}
                    <div className="col-12 lg:col-8">
                        <Card className="shadow-1 mb-2 md:mb-3">
                            {/* Bank header — compact initial avatar with subtle ring */}
                            <div className="flex align-items-center gap-2 mb-2 pb-2 border-bottom-1 surface-border">
                                <div
                                    className="flex align-items-center justify-content-center border-round bg-primary-50 text-primary font-bold flex-shrink-0 border-2 border-primary-100"
                                    style={{ width: '2.75rem', height: '2.75rem', fontSize: '1.1rem' }}
                                    aria-hidden="true"
                                >
                                    {bank_name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <h2 className="text-900 font-semibold text-lg m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis">{bank_name}</h2>
                                        {lender_type && lender_type !== 'bank' && (
                                            <Tag
                                                value={lender_type.toUpperCase()}
                                                severity={LENDER_TYPE_SEVERITY[lender_type] || 'secondary'}
                                                className="text-xs"
                                                style={{ fontSize: '0.6rem', padding: '0.15rem 0.5rem' }}
                                            />
                                        )}
                                    </div>
                                    <div className="text-600 text-sm flex align-items-center gap-1 flex-wrap">
                                        <i className={`pi ${loanTypeConfig.icon || 'pi-tag'} text-primary`} style={{ fontSize: '0.8rem' }} aria-hidden="true" />
                                        <span className="font-medium">{loan_type_category || loanTypeConfig.label}</span>
                                        {segment && (<><span className="text-400">·</span><span>{segment}</span></>)}
                                    </div>
                                    <div className="flex align-items-center gap-1 text-700 text-xs mt-1 min-w-0">
                                        <i className="pi pi-map-marker text-primary flex-shrink-0" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                        <span className={`font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis ${locationLabel ? 'text-700' : 'text-400'}`}>
                                            {locationLabel || 'Pan-India'}
                                        </span>
                                        {service_areas.length > 0 && (
                                            <span className="text-500 text-xs flex-shrink-0">· +{service_areas.length} more</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Commission band — soft green, compact two-column */}
                            <div
                                className="border-round border-1 p-2 mb-2"
                                style={{ backgroundColor: 'var(--green-50)', borderColor: 'var(--green-100)' }}
                            >
                                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-600 text-xs font-medium uppercase" style={{ letterSpacing: '0.04em' }}>
                                            {isOwner ? "You'll pay" : 'Commission'}
                                        </div>
                                        <div className="text-green-700 font-bold line-height-1" style={{ fontSize: '1.5rem' }}>
                                            {formatCommission(commission_percentage)}
                                        </div>
                                        <div className="text-600 text-xs mt-1">{payout_on || 'on disbursed amount'}</div>
                                    </div>
                                    {hasEstPayout && (
                                        <>
                                            <div className="hidden md:block" style={{ borderLeft: '1px solid var(--green-200)', alignSelf: 'stretch', margin: '0 0.5rem' }} />
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-600 text-xs font-medium uppercase" style={{ letterSpacing: '0.04em' }}>
                                                    Est. earning
                                                </div>
                                                <div className="text-green-700 font-bold text-base white-space-nowrap line-height-1 mt-1">
                                                    {inr(est_payout_min)}–{inr(est_payout_max)}
                                                </div>
                                                {max_payout_cap && (
                                                    <div className="text-600 text-xs mt-1">cap {inr(max_payout_cap)}</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {(payout_type || payout_timeline) && (
                                    <div className="flex align-items-center gap-2 mt-2 pt-2 flex-wrap" style={{ borderTop: '1px solid var(--green-200)' }}>
                                        {payout_type && (
                                            <Tag
                                                value={payout_type.charAt(0).toUpperCase() + payout_type.slice(1)}
                                                severity={payout_type === 'instant' ? 'success' : payout_type === 'advance' ? 'info' : 'secondary'}
                                                className="text-xs capitalize"
                                                icon={payout_type === 'instant' ? 'pi pi-bolt' : payout_type === 'advance' ? 'pi pi-clock' : 'pi pi-calendar'}
                                                style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}
                                            />
                                        )}
                                        {payout_timeline && <span className="text-700 text-xs">{payout_timeline}</span>}
                                    </div>
                                )}
                            </div>

                            {/* Quick stats grid — icon chip + value + label (P15 pattern) */}
                            <div className="grid">
                                {[
                                    { icon: 'pi-eye', value: view_count, label: 'Views', accent: 'primary' },
                                    { icon: 'pi-send', value: invite_count, label: 'Enquiries', accent: 'primary' },
                                    { icon: 'pi-check-circle', value: acceptance_count, label: 'Closed', accent: 'success' },
                                    {
                                        icon: 'pi-clock',
                                        value: deadlineInfo.daysLeft != null ? `${deadlineInfo.daysLeft}d` : '—',
                                        label: deadlineInfo.daysLeft != null ? 'Closes in' : 'No deadline',
                                        accent: deadlineInfo.severity === 'danger' ? 'danger'
                                            : deadlineInfo.severity === 'warning' ? 'warning'
                                            : 'primary',
                                    },
                                ].map((s, i) => {
                                    const valueColour = s.accent === 'danger' ? 'text-red-500'
                                        : s.accent === 'warning' ? 'text-orange-500'
                                        : 'text-900';
                                    const chipBg = s.accent === 'danger' ? 'bg-red-50 text-red-500'
                                        : s.accent === 'warning' ? 'bg-orange-50 text-orange-500'
                                        : s.accent === 'success' ? 'bg-green-50 text-green-600'
                                        : 'bg-primary-50 text-primary';
                                    return (
                                        <div key={i} className="col-6 md:col-3">
                                            <div className="surface-card border-round border-1 surface-border p-2 h-full flex align-items-center gap-2">
                                                <div className={`flex align-items-center justify-content-center border-round flex-shrink-0 ${chipBg}`} style={{ width: '1.75rem', height: '1.75rem' }}>
                                                    <i className={`pi ${s.icon}`} style={{ fontSize: '0.8rem' }} aria-hidden="true" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className={`font-bold text-sm line-height-1 ${valueColour}`}>{s.value}</div>
                                                    <div className="text-500 text-xs mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">{s.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>

                        {/* Payout Structure */}
                        <Panel
                            toggleable
                            pt={{ content: { className: "py-2 px-3" }, header: { className: "py-2 px-3" } }}
                            className="shadow-1 mb-2"
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-percentage text-primary" aria-hidden="true" />
                                    Payout Structure
                                    {slabs.length > 0 && <Tag value={`${slabs.length} ${slabs.length === 1 ? 'tier' : 'tiers'}`} severity="info" className="text-xs" />}
                                </span>
                            }
                        >
                            <div className="grid">
                                <div className="col-6 md:col-3">
                                    <div className="text-500 text-xs">Commission</div>
                                    <div className="text-900 font-semibold text-sm">{formatCommission(commission_percentage)}</div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="text-500 text-xs">Payout type</div>
                                    <div className={`font-semibold text-sm capitalize ${payout_type ? 'text-900' : 'text-400'}`}>{payout_type || 'Not specified'}</div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="text-500 text-xs">Payout on</div>
                                    <div className={`font-semibold text-sm ${payout_on ? 'text-900' : 'text-400'}`}>{payout_on || 'Not specified'}</div>
                                </div>
                                <div className="col-6 md:col-3">
                                    <div className="text-500 text-xs">Max payout cap</div>
                                    <div className={`font-semibold text-sm ${max_payout_cap ? 'text-900' : 'text-400'}`}>{max_payout_cap ? inr(max_payout_cap) : 'No cap'}</div>
                                </div>
                                <div className="col-12">
                                    <div className="text-500 text-xs">Payout timeline</div>
                                    <div className={`text-sm ${payout_timeline ? 'text-700' : 'text-400'}`}>{payout_timeline || 'Not specified'}</div>
                                </div>
                                {slabs.length > 0 ? (
                                    <div className="col-12 mt-2">
                                        <div className="text-500 text-xs mb-2">Tiered commission slabs</div>
                                        <div className="surface-50 border-round p-2">
                                            {slabs.map((slab, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`flex justify-content-between align-items-center py-2 px-1 ${idx < slabs.length - 1 ? 'border-bottom-1 surface-border' : ''}`}
                                                >
                                                    <span className="flex align-items-center gap-2 text-700 text-sm">
                                                        {/* Tier-number badge (Stripe / AWS pricing pattern) —
                                                            shows these are tiers, NOT expandable rows. */}
                                                        <span
                                                            className="flex align-items-center justify-content-center border-round-2xl bg-primary-50 text-primary font-bold flex-shrink-0"
                                                            style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.7rem' }}
                                                            aria-hidden="true"
                                                        >
                                                            {idx + 1}
                                                        </span>
                                                        <span>{slab.amount_upto ? `Up to ${inr(slab.amount_upto)}` : 'Above previous tier'}</span>
                                                    </span>
                                                    <span className="text-green-700 font-semibold text-sm">{slab.pct}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="col-12 mt-2 text-500 text-xs">Flat commission across all loan amounts.</div>
                                )}
                            </div>
                        </Panel>

                        {/* Eligibility Criteria */}
                        <Panel
                            toggleable
                            pt={{ content: { className: "py-2 px-3" }, header: { className: "py-2 px-3" } }}
                            className="shadow-1 mb-2"
                            header={<span className="flex align-items-center gap-2 text-sm font-medium"><i className="pi pi-id-card text-primary" aria-hidden="true" />Eligibility Criteria</span>}
                        >
                            <div className="grid">
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Loan range</div>
                                    <div className={`font-semibold text-sm ${(min_loan_amount && max_loan_amount) ? 'text-900' : 'text-400'}`}>
                                        {(min_loan_amount && max_loan_amount) ? formatLoanRange(min_loan_amount, max_loan_amount) : 'Not specified'}
                                    </div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Min CIBIL</div>
                                    <div className={`font-semibold text-sm ${min_cibil ? 'text-900' : 'text-400'}`}>{min_cibil ? `${min_cibil}+` : 'Any score'}</div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Employment</div>
                                    <div className={`font-semibold text-sm ${employmentLabel ? 'text-900' : 'text-400'}`}>{employmentLabel || 'Any'}</div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Age range</div>
                                    <div className={`font-semibold text-sm ${(min_age || max_age) ? 'text-900' : 'text-400'}`}>
                                        {(min_age || max_age) ? `${min_age || '—'}–${max_age || '—'} years` : 'Any age'}
                                    </div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Min monthly income</div>
                                    <div className={`font-semibold text-sm ${min_monthly_income ? 'text-900' : 'text-400'}`}>
                                        {min_monthly_income ? `${inr(min_monthly_income)} / mo` : 'No minimum'}
                                    </div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Tenure</div>
                                    <div className={`font-semibold text-sm ${(tenure_months_min || tenure_months_max) ? 'text-900' : 'text-400'}`}>
                                        {(tenure_months_min || tenure_months_max) ? `${tenure_months_min || '—'}–${tenure_months_max || '—'} months` : 'Flexible'}
                                    </div>
                                </div>
                            </div>
                        </Panel>

                        {/* Operations & Trust */}
                        <Panel
                            toggleable
                            pt={{ content: { className: "py-2 px-3" }, header: { className: "py-2 px-3" } }}
                            className="shadow-1 mb-2"
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-cog text-primary" aria-hidden="true" />
                                    Operations & Trust
                                    {digital_journey && <Tag value="Digital" severity="success" icon="pi pi-bolt" className="text-xs" />}
                                </span>
                            }
                        >
                            <div className="grid">
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Approval rate</div>
                                    <div className={`font-semibold text-sm ${approvalPct != null ? 'text-900' : 'text-400'}`}>
                                        {approvalPct != null ? `${Math.round(approvalPct)}%` : 'Not published'}
                                    </div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Processing time</div>
                                    <div className={`font-semibold text-sm ${processing_time ? 'text-900' : 'text-400'}`}>{processing_time || 'Not specified'}</div>
                                </div>
                                <div className="col-6 md:col-4">
                                    <div className="text-500 text-xs">Digital journey</div>
                                    <div className={`font-semibold text-sm ${digital_journey ? 'text-green-600' : 'text-500'}`}>
                                        {digital_journey
                                            ? <><i className="pi pi-bolt mr-1" aria-hidden="true" />Fully digital</>
                                            : 'Manual / Mixed'}
                                    </div>
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="text-500 text-xs mb-2">Documents required</div>
                                    {docsRequired.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {docsRequired.map((d, i) => <Tag key={i} value={d} severity="info" className="text-xs" />)}
                                        </div>
                                    ) : (
                                        <div className="text-400 text-sm">Not specified</div>
                                    )}
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="text-500 text-xs">Foreclosure norms</div>
                                    <div className={`text-sm ${foreclosure_norms ? 'text-700' : 'text-400'}`}>{foreclosure_norms || 'Not specified'}</div>
                                </div>
                            </div>
                        </Panel>

                        {/* Coverage & Specializations */}
                        <Panel
                            toggleable
                            pt={{ content: { className: "py-2 px-3" }, header: { className: "py-2 px-3" } }}
                            className="shadow-1 mb-2"
                            header={<span className="flex align-items-center gap-2 text-sm font-medium"><i className="pi pi-map text-primary" aria-hidden="true" />Coverage & Specializations</span>}
                        >
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="text-500 text-xs mb-2">Service areas</div>
                                    {service_areas.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {service_areas.map((a, i) => <Tag key={i} value={a} severity="secondary" className="text-xs" />)}
                                        </div>
                                    ) : (
                                        <div className="text-400 text-sm">Primary location only</div>
                                    )}
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="text-500 text-xs mb-2">Specializations</div>
                                    {specializations.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {specializations.map((s, i) => <Tag key={i} value={s} severity="success" className="text-xs" />)}
                                        </div>
                                    ) : (
                                        <div className="text-400 text-sm">None listed</div>
                                    )}
                                </div>
                            </div>
                        </Panel>

                        {/* Timeline */}
                        <Panel
                            toggleable
                            collapsed
                            pt={{ content: { className: "py-2 px-3" }, header: { className: "py-2 px-3" } }}
                            className="shadow-1 mb-2"
                            header={<span className="flex align-items-center gap-2 text-sm font-medium"><i className="pi pi-calendar text-primary" aria-hidden="true" />Timeline</span>}
                        >
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <div className="text-500 text-xs">Disbursement deadline</div>
                                    <div className={`font-semibold text-sm ${deadlineInfo.severity === 'danger' ? 'text-red-500' : deadlineInfo.severity === 'warning' ? 'text-orange-500' : 'text-900'}`}>
                                        {fmtDate(disbursement_deadline) || 'Not specified'}
                                        {deadlineInfo.label && <span className="text-500 font-normal ml-1">({deadlineInfo.label})</span>}
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="text-500 text-xs">Valid until</div>
                                    <div className={`font-semibold text-sm ${valid_until ? 'text-900' : 'text-400'}`}>{fmtDate(valid_until) || '—'}</div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="text-500 text-xs">Posted</div>
                                    <div className="text-700">{formatTimeAgo(created_at)}</div>
                                </div>
                            </div>
                        </Panel>

                    </div>

                    {/* ── Top zone: col-4 Sidebar (DSA profile + Action) ── */}
                    <div className="col-12 lg:col-4">
                        <div className="sticky" style={{ top: '0.5rem' }}>
                        {/* DSA Profile Card — profile-card feel with centered avatar */}
                        <Card className="shadow-1 mb-2 md:mb-3">
                            <div className="text-500 text-xs font-medium uppercase mb-3" style={{ letterSpacing: '0.04em' }}>
                                Listed by
                            </div>
                            <div className="text-center mb-3">
                                <Avatar
                                    image={dsaPhoto}
                                    icon={!dsaPhoto ? 'pi pi-user' : undefined}
                                    shape="circle"
                                    className="bg-primary-100 border-2 border-primary-100"
                                    style={{ width: '4rem', height: '4rem', fontSize: '1.5rem' }}
                                />
                                <div className="mt-2 flex align-items-center justify-content-center gap-1 flex-wrap">
                                    <span className="text-900 font-semibold text-base">{dsaName}</span>
                                    {isVerified && <i className="pi pi-verified text-primary" style={{ fontSize: '0.9rem' }} aria-label="Verified" />}
                                </div>
                                {dsaHeadline && (
                                    <div className="text-500 text-xs mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">{dsaHeadline}</div>
                                )}
                            </div>

                            {/* DSA stat row — 3-up grid with subtle dividers */}
                            {(dsaRating > 0 || dsaDeals > 0 || dsaExperience) && (
                                <div className="flex justify-content-around mb-3 py-2 surface-50 border-round">
                                    {dsaRating != null && dsaRating > 0 && (
                                        <div className="text-center flex-1">
                                            <div className="flex align-items-center justify-content-center gap-1">
                                                <i className="pi pi-star-fill text-yellow-500" style={{ fontSize: '0.85rem' }} aria-hidden="true" />
                                                <span className="font-bold text-900 text-sm">{dsaRating.toFixed(1)}</span>
                                            </div>
                                            <div className="text-500 text-xs mt-1">Rating</div>
                                        </div>
                                    )}
                                    {dsaDeals > 0 && (
                                        <div className="text-center flex-1 border-left-1 surface-border">
                                            <div className="font-bold text-900 text-sm">{dsaDeals}</div>
                                            <div className="text-500 text-xs mt-1">Deals</div>
                                        </div>
                                    )}
                                    {dsaExperience && (
                                        <div className="text-center flex-1 border-left-1 surface-border">
                                            <div className="font-bold text-900 text-sm">{dsaExperience} yr</div>
                                            <div className="text-500 text-xs mt-1">Experience</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Additional profile facts — clean key/value rows with icons */}
                            <div className="text-xs">
                                <div className="flex align-items-center gap-2 py-2 border-bottom-1 surface-border">
                                    <i className="pi pi-calendar text-primary flex-shrink-0" style={{ fontSize: '0.75rem', width: '0.85rem' }} aria-hidden="true" />
                                    <span className="text-500 flex-shrink-0">Member since</span>
                                    <span className={`font-medium ml-auto text-right ${dsaMemberSince ? 'text-700' : 'text-400'}`}>{dsaMemberSince || '—'}</span>
                                </div>
                                <div className="flex align-items-center gap-2 py-2 border-bottom-1 surface-border">
                                    <i className="pi pi-comment text-primary flex-shrink-0" style={{ fontSize: '0.75rem', width: '0.85rem' }} aria-hidden="true" />
                                    <span className="text-500 flex-shrink-0">Responds in</span>
                                    <span className={`font-medium ml-auto text-right ${dsaResponse ? 'text-700' : 'text-400'}`}>{dsaResponse || 'Not enough data'}</span>
                                </div>
                                <div className="flex align-items-center gap-2 py-2 border-bottom-1 surface-border">
                                    <i className="pi pi-indian-rupee text-primary flex-shrink-0" style={{ fontSize: '0.75rem', width: '0.85rem' }} aria-hidden="true" />
                                    <span className="text-500 flex-shrink-0">Total disbursed</span>
                                    <span className={`font-medium ml-auto text-right ${dsaDisbursed && Number(dsaDisbursed) > 0 ? 'text-700' : 'text-400'}`}>
                                        {dsaDisbursed && Number(dsaDisbursed) > 0 ? inr(dsaDisbursed) : '—'}
                                    </span>
                                </div>
                                <div className="flex align-items-start gap-2 py-2">
                                    <i className="pi pi-globe text-primary flex-shrink-0 mt-1" style={{ fontSize: '0.75rem', width: '0.85rem' }} aria-hidden="true" />
                                    <span className="text-500 flex-shrink-0 mt-1">Languages</span>
                                    <span className="ml-auto text-right">
                                        {dsaLanguages.length > 0 ? (
                                            <span className="flex flex-wrap gap-1 justify-content-end">
                                                {dsaLanguages.map((l, i) => (
                                                    <Tag key={i} value={l} severity="secondary" className="text-xs" style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }} />
                                                ))}
                                            </span>
                                        ) : (
                                            <span className="text-400 font-medium">—</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* Action Card (sticky) — primary CTA with reassurance subtitle */}
                        <Card className="shadow-1 sticky" style={{ top: '0.5rem' }}>
                            {isOwner ? (
                                <div className="flex flex-column gap-2">
                                    <Button label="Edit offer" icon="pi pi-pencil" className="w-full"
                                        onClick={() => navigate(`/app/dsa-offers/edit/${id}`)} />
                                    {status === 'active' && (
                                        <Button label="Pause" icon="pi pi-pause" severity="warning" outlined className="w-full" size="small"
                                            onClick={handlePause} loading={actionLoading} />
                                    )}
                                    {status === 'paused' && (
                                        <Button label="Resume" icon="pi pi-play" severity="success" outlined className="w-full" size="small"
                                            onClick={handleResume} loading={actionLoading} />
                                    )}
                                    <Button label="Delete offer" icon="pi pi-trash" severity="danger" outlined className="w-full" size="small"
                                        onClick={handleDelete} loading={actionLoading} />
                                </div>
                            ) : (
                                <div className="hidden lg:block">
                                    <Button
                                        label="Send enquiry"
                                        icon="pi pi-send"
                                        severity="success"
                                        className="w-full"
                                        onClick={() => setShowInviteDialog(true)}
                                    />
                                    <div className="text-center text-500 text-xs mt-2 flex align-items-center justify-content-center gap-1">
                                        <i className="pi pi-check-circle text-green-600" aria-hidden="true" />
                                        <span>Free · No commitment</span>
                                    </div>
                                </div>
                            )}
                            <div className="border-top-1 surface-border mt-3 pt-2 flex align-items-center justify-content-between text-500 text-xs">
                                <span className="flex align-items-center gap-1">
                                    <i className="pi pi-clock" aria-hidden="true" />Posted {formatTimeAgo(created_at)}
                                </span>
                                {view_count > 0 && (
                                    <span className="flex align-items-center gap-1">
                                        <i className="pi pi-eye" aria-hidden="true" />{view_count}
                                    </span>
                                )}
                            </div>
                        </Card>
                        </div>{/* /sticky wrapper */}
                    </div>{/* /col-4 */}
                </div>{/* /inner grid (top zone) */}

                {/* (panels moved into col-8 above so the right sidebar can stick) */}

                {/* ── Mobile sticky Enquire bar ── */}
                {!isOwner && (
                    <>
                        <div className="fixed bottom-0 left-0 right-0 p-2 surface-card shadow-8 lg:hidden" style={{ zIndex: 1000 }}>
                            <Button label="Send enquiry" icon="pi pi-send" severity="success" className="w-full" onClick={() => setShowInviteDialog(true)} />
                        </div>
                        <div className="h-4rem lg:hidden" />
                    </>
                )}

                {/* ── Invite Dialog ── */}
                {showInviteDialog && (
                    <InviteToLeadDialog
                        visible={showInviteDialog}
                        onHide={() => setShowInviteDialog(false)}
                        dsaOffer={offer}
                        onSuccess={handleInviteSuccess}
                    />
                )}
            </div>
        </div>
    );
};

export default OfferDetail;
